import React, { useEffect, useMemo, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { get_branch_calendar } from "../../../api/Calendar";
import EventForm from "../Events/EventForm";

const localizer = momentLocalizer(moment);

// Type → color. Matches the student-side palette to keep the brand
// consistent across portals.
const TYPE_STYLES = {
  lesson: { bg: "#2563eb", label: "Class" },
  event: { bg: "#A6192E", label: "Event" },
  test: { bg: "#7c3aed", label: "Test" },
};

// Build a JS Date from "YYYY-MM-DD" + "HH:MM:SS".
const combine = (date, time) => {
  if (!date) return null;
  return new Date(`${date}T${time || "00:00:00"}`);
};

// Period bounds for the current view → drives the API date range.
const monthBoundsFromDate = (d) => {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const pad = (n) => String(n).padStart(2, "0");
  const toYmd = (x) =>
    `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
  // pull a slightly wider window to cover days that overflow the month view
  const widened = new Date(first);
  widened.setDate(widened.getDate() - 7);
  const widenedEnd = new Date(last);
  widenedEnd.setDate(widenedEnd.getDate() + 7);
  return { from: toYmd(widened), to: toYmd(widenedEnd) };
};

const BranchCalendar = () => {
  const branchId = localStorage.getItem("b_id");
  const [rawLessons, setRawLessons] = useState([]);
  const [rawEvents, setRawEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewDate, setViewDate] = useState(new Date());
  // Initial payload for the create modal when user clicks an empty slot.
  // Null = modal closed. Object = modal open with these pre-filled values.
  const [createInitial, setCreateInitial] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!branchId) return;
    let cancelled = false;
    const { from, to } = monthBoundsFromDate(viewDate);
    (async () => {
      const res = await get_branch_calendar({ branch_id: branchId, from, to });
      if (cancelled) return;
      setRawLessons(res?.data?.lessons || []);
      setRawEvents(res?.data?.events || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [branchId, viewDate, reloadKey]);

  const pad2 = (n) => String(n).padStart(2, "0");
  const dateOnly = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const timeOnly = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

  const handleSlotSelect = ({ start, end }) => {
    // Month-view all-day click gives midnight; treat as date-only with no time.
    const isAllDay = start.getHours() === 0 && start.getMinutes() === 0 &&
                     end.getHours() === 0 && end.getMinutes() === 0;
    setCreateInitial({
      date: dateOnly(start),
      time_start: isAllDay ? "" : timeOnly(start),
      time_end: isAllDay ? "" : timeOnly(end),
    });
  };

  // Convert API payload to react-big-calendar event objects.
  const events = useMemo(() => {
    const lessons = (rawLessons || []).map((l) => ({
      id: `lesson-${l.class_id}-${l.date}`,
      title: l.class_name,
      start: combine(l.date, l.start_time),
      end: combine(l.date, l.end_time),
      kind: "lesson",
      payload: l,
    }));
    const evts = (rawEvents || []).map((e) => ({
      id: `event-${e.id}`,
      title: e.title,
      start: combine(e.date, e.time_start),
      end: combine(e.date, e.time_end),
      kind: e.event_type === "test" ? "test" : "event",
      payload: e,
    }));
    return [...lessons, ...evts].filter((x) => x.start && x.end);
  }, [rawLessons, rawEvents]);

  const eventStyleGetter = (ev) => {
    const t = TYPE_STYLES[ev.kind] || TYPE_STYLES.event;
    return {
      style: {
        backgroundColor: t.bg,
        color: "#ffffff",
        borderRadius: 5,
        border: "none",
        padding: "2px 5px",
      },
    };
  };

  const handleClose = () => setSelected(null);

  return (
    <div className="right-nav" id="dash-event">
      <style>{`
        .rbc-toolbar { font-size: 13px; margin-bottom: 12px; }
        .rbc-toolbar button {
          font-size: 12px;
          padding: 4px 10px;
          font-weight: 500;
          color: #374151;
          border-color: #e5e7eb;
        }
        .rbc-toolbar button:hover { background: #f3f4f6; border-color: #d1d5db; }
        .rbc-toolbar button.rbc-active {
          background: #e5e7eb; border-color: #d1d5db; box-shadow: none;
        }
        .rbc-toolbar-label { font-size: 14px; font-weight: 600; color: #1f2937; }
        .rbc-header { font-size: 12px; font-weight: 500; color: #6b7280; padding: 6px 0; }
      `}</style>

      <div style={{ padding: "20px 24px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <h4 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: "#1a1a2e" }}>
            Branch Calendar
          </h4>
          <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
            {Object.entries(TYPE_STYLES).map(([key, t]) => (
              <span
                key={key}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#475569" }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: t.bg,
                    display: "inline-block",
                  }}
                />
                {t.label}
              </span>
            ))}
          </div>
        </div>

        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "75vh" }}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={setSelected}
          selectable
          onSelectSlot={handleSlotSelect}
          longPressThreshold={150}
          onNavigate={(d) => setViewDate(d)}
          tooltipAccessor={(ev) => ev.title}
        />

        <p style={{ fontSize: 12, color: "#94a3b8", margin: "10px 0 0" }}>
          Tip: click an empty day or drag a time range to create an event or
          test on the spot.
        </p>
      </div>

      {createInitial && (
        <div
          onClick={() => setCreateInitial(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1060,
            padding: 24,
            overflowY: "auto",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: 10,
              maxWidth: 720,
              width: "100%",
              padding: "20px 24px",
              boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
              maxHeight: "calc(100vh - 48px)",
              overflowY: "auto",
            }}
          >
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 14, gap: 12,
            }}>
              <h5 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#1f2937" }}>
                New Event / Test
                <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 400, color: "#6b7280" }}>
                  on {createInitial.date}
                </span>
              </h5>
              <button
                type="button"
                onClick={() => setCreateInitial(null)}
                aria-label="Close"
                style={{
                  background: "transparent", border: "none", fontSize: 22,
                  lineHeight: 1, cursor: "pointer", color: "#94a3b8", padding: 0,
                }}
              >
                ×
              </button>
            </div>
            <EventForm
              initial={createInitial}
              onCancel={() => setCreateInitial(null)}
              onSaved={() => {
                setCreateInitial(null);
                setReloadKey((k) => k + 1);
              }}
              submitLabel="Create"
            />
          </div>
        </div>
      )}

      {selected && (
        <div
          onClick={handleClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: 10,
              maxWidth: 420,
              width: "calc(100% - 32px)",
              padding: "20px 24px",
              boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 12,
                gap: 12,
              }}
            >
              <h5 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#1f2937" }}>
                {selected.title}
              </h5>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close"
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 22,
                  lineHeight: 1,
                  cursor: "pointer",
                  color: "#94a3b8",
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>
            {selected.kind === "lesson" ? (
              <>
                <p style={{ margin: "0 0 6px", fontSize: 13 }}>
                  <strong>Course:</strong> {selected.payload.course_name || "—"}
                </p>
                <p style={{ margin: "0 0 6px", fontSize: 13 }}>
                  <strong>Date:</strong>{" "}
                  {moment(selected.start).format("ddd, MMM D YYYY")}
                </p>
                <p style={{ margin: "0 0 6px", fontSize: 13 }}>
                  <strong>Time:</strong>{" "}
                  {moment(selected.start).format("h:mm A")} –{" "}
                  {moment(selected.end).format("h:mm A")}
                </p>
                {selected.payload.instructor_name && (
                  <p style={{ margin: "0 0 6px", fontSize: 13 }}>
                    <strong>Instructor:</strong> {selected.payload.instructor_name}
                  </p>
                )}
                {selected.payload.week_index && (
                  <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
                    Week {selected.payload.week_index}
                  </p>
                )}
              </>
            ) : (
              <>
                <p style={{ margin: "0 0 6px", fontSize: 13 }}>
                  <strong>Date:</strong>{" "}
                  {moment(selected.start).format("ddd, MMM D YYYY")}
                </p>
                {selected.payload?.time_start && (
                  <p style={{ margin: "0 0 6px", fontSize: 13 }}>
                    <strong>Time:</strong>{" "}
                    {moment(selected.start).format("h:mm A")} –{" "}
                    {moment(selected.end).format("h:mm A")}
                  </p>
                )}
                {selected.payload?.venue_name && (
                  <p style={{ margin: "0 0 6px", fontSize: 13 }}>
                    <strong>Venue:</strong> {selected.payload.venue_name}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchCalendar;
